import type { ProductLocale } from '@/modules/auth/config/productBranding';
import { normalizeProductCode } from '@/modules/auth/config/productBranding';

function readSearchParam(url: URL, key: string): string | null {
  const value = url.searchParams.get(key);
  return value?.trim() ? value.trim() : null;
}

function parseReturnUrl(returnUrl: string | null | undefined): URL | null {
  if (!returnUrl?.trim()) return null;
  const trimmed = returnUrl.trim();
  // Only accept relative paths to prevent open-redirect parameter extraction
  if (!trimmed.startsWith('/')) return null;
  try {
    return new URL(trimmed, window.location.origin);
  } catch {
    return null;
  }
}

function resolveLocale(value: string | null | undefined): ProductLocale | null {
  if (!value) return null;
  const normalized = value.toLowerCase();
  if (normalized.startsWith('en')) return 'en';
  if (normalized.startsWith('ar')) return 'ar';
  return null;
}

export interface ProductAuthContext {
  productCode: string | null;
  locale: ProductLocale | null;
}

export function parseProductAuthContext(search: Record<string, unknown>): ProductAuthContext {
  const directProduct =
    (typeof search.product === 'string' ? search.product : null) ??
    (typeof search.product_slug === 'string' ? search.product_slug : null);

  const returnParsed = parseReturnUrl(typeof search.return === 'string' ? search.return : null);

  const productFromReturn =
    returnParsed &&
    (readSearchParam(returnParsed, 'product') ?? readSearchParam(returnParsed, 'product_slug'));

  const localeFromSearch = resolveLocale(typeof search.locale === 'string' ? search.locale : null);
  const localeFromReturn = returnParsed && resolveLocale(readSearchParam(returnParsed, 'locale'));

  return {
    productCode: normalizeProductCode(directProduct ?? productFromReturn),
    locale: localeFromSearch ?? localeFromReturn,
  };
}
